import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { DirectionalToaster } from './components/common/DirectionalToaster';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import { DirectionProvider } from './i18n/direction';
import { UITextProvider } from './hooks/useUIText';
import { FullPageLoading } from './components/common/Loading';
import DashboardLayout from './components/layout/DashboardLayout';
import NotificationRealtimeBridge from './components/notifications/NotificationRealtimeBridge';

const CallerWorkspace = React.lazy(() => import('./pages/caller/CallerWorkspace'));
const CallerManagement = React.lazy(() => import('./pages/admin/CallerManagement'));
// Pages
const Landing = React.lazy(() => import('./pages/Landing'));
const Login = React.lazy(() => import('./pages/Login'));
const GarageProfile = React.lazy(() => import('./pages/GarageProfile'));
const ChatsList = React.lazy(() => import('./pages/ChatsList'));
const ChatRoom = React.lazy(() => import('./pages/ChatRoom'));
const MessagesPage = React.lazy(() => import('./pages/customer/MessagesPage'));
import RouteErrorBoundary from './components/common/RouteErrorBoundary';
const SavedSearches = React.lazy(() => import('./pages/customer/SavedSearches'));
const MyRequests = React.lazy(() => import('./pages/MyRequests'));
const RequestDetail = React.lazy(() => import('./pages/RequestDetail'));

// Customer Pages
const CustomerDashboard = React.lazy(() => import('./pages/customer/Dashboard'));
const SearchGarages = React.lazy(() => import('./pages/customer/Search'));
const VehiclesList = React.lazy(() => import('./pages/customer/VehiclesList'));
const VehicleForm = React.lazy(() => import('./pages/customer/VehicleForm'));
const VehicleProfile = React.lazy(() => import('./pages/customer/VehicleProfile'));
const MyPosts = React.lazy(() => import('./pages/customer/MyPosts'));
const PostDetails = React.lazy(() => import('./pages/customer/PostDetails'));
const BookingsList = React.lazy(() => import('./pages/customer/BookingsList'));
const BookingDetails = React.lazy(() => import('./pages/customer/BookingDetails'));
const VINDecodePage = React.lazy(() => import('./pages/customer/VINDecodePage'));
const UserProfile = React.lazy(() => import('./pages/customer/UserProfile'));
const EnrollVehicleCarePage = React.lazy(() => import('./pages/customer/care/EnrollVehicleCarePage'));
const CareSuccess = React.lazy(() => import('./pages/customer/care/CareSuccess'));
const CareCancel = React.lazy(() => import('./pages/customer/care/CareCancel'));
const InvoicesPage = React.lazy(() => import('./pages/customer/billing/InvoicesPage'));

// Repair Flow (route-per-step)
const RepairFlow = React.lazy(() => import('./pages/repair/RepairFlow'));

// Parts Flow (route-per-step)
const PartsFlow = React.lazy(() => import('./pages/parts/PartsFlow'));
const RequestTypeSelect = React.lazy(() => import('./pages/RequestTypeSelect'));

// Garage Pages
const GarageDashboard = React.lazy(() => import('./pages/garage/Dashboard'));
const GarageOnboarding = React.lazy(() => import('./pages/garage/Onboarding'));
// UAT-1: provider-side `/garage/leads` and `/dealer/leads` retired.
// "Incoming Repairs" / "Incoming Requests" pages are now the providers'
// leads view. The acquisition Leads system stays admin-only at
// `/admin/leads`. The two dashboard files below have been deleted; their
// imports are intentionally removed here.
const GarageBookings = React.lazy(() => import('./pages/garage/BookingsManagement'));
const JobCardsList = React.lazy(() => import('./pages/garage/JobCardsList'));
const JobCardManagement = React.lazy(() => import('./pages/garage/JobCardManagement'));

// Garage Source Parts Flow (route-per-step)
const GaragePartsFlow = React.lazy(() => import('./pages/garage/GaragePartsFlow'));
const MyRfqs = React.lazy(() => import('./pages/garage/MyRfqs'));
const RfqDetail = React.lazy(() => import('./pages/garage/RfqDetail'));

// Dealer → Dealer RFQ (D2D parts sourcing)
const DealerSourceParts = React.lazy(() => import('./pages/dealer/DealerSourceParts'));
const DealerMyRfqs = React.lazy(() => import('./pages/dealer/DealerMyRfqs'));
const DealerRfqDetail = React.lazy(() => import('./pages/dealer/DealerRfqDetail'));

// Unified Provider Onboarding
const ProviderOnboarding = React.lazy(() => import('./pages/provider/Onboarding'));
const ProviderSubscription = React.lazy(() => import('./pages/provider/Subscription'));
const ProviderProfile = React.lazy(() => import('./pages/provider/Profile'));
const ProviderAccountSettings = React.lazy(() => import('./pages/provider/AccountSettings'));
const ProviderMyPlan = React.lazy(() => import('./pages/provider/MyPlan'));
const ProviderMyUsage = React.lazy(() => import('./pages/provider/MyUsage'));
const ProviderMyBilling = React.lazy(() => import('./pages/provider/MyBilling'));
const SubscriptionSuccess = React.lazy(() => import('./pages/provider/SubscriptionSuccess'));
// NOTE: Standalone provider VIN Lookup has been retired. VIN decoding remains
// available inside customer / request / vehicle flows and admin tooling only.

// Dealer Pages
const IncomingRequests = React.lazy(() => import('./pages/dealer/IncomingRequests'));
const DealerRequestDetail = React.lazy(() => import('./pages/dealer/DealerRequestDetail'));
const DealerDashboard = React.lazy(() => import('./pages/dealer/DealerDashboard'));

// Garage Repair Pages (Phase 7)
const IncomingRepairRequests = React.lazy(() => import('./pages/garage_repairs/IncomingRepairRequests'));
const GarageRepairRequestDetail = React.lazy(() => import('./pages/garage_repairs/GarageRepairRequestDetail'));

// Admin Pages
const AdminDashboardEnhanced = React.lazy(() => import('./pages/admin/DashboardEnhanced'));
const AdminProviders = React.lazy(() => import('./pages/admin/Providers'));
const AdminProviderDetail = React.lazy(() => import('./pages/admin/ProviderDetail'));
const AdminRequests = React.lazy(() => import('./pages/admin/Requests'));
const AdminMasterData = React.lazy(() => import('./pages/admin/MasterData'));
const AdminAuditLogs = React.lazy(() => import('./pages/admin/AuditLogs'));
const AdminVehicleOwnership = React.lazy(() => import('./pages/admin/VehicleOwnership'));
const AdminProviderClaims = React.lazy(() => import('./pages/admin/ProviderClaims'));
const AdminCurrencies = React.lazy(() => import('./pages/admin/Currencies'));
import AdminLayout from './components/layout/AdminLayout';
const AdminSystemSettings = React.lazy(() => import('./pages/admin/SystemSettings'));
const AdminFirebase = React.lazy(() => import('./pages/admin/AdminFirebase'));
const AdminKYCVerification = React.lazy(() => import('./pages/admin/KYCVerification'));
const AdminKycExpiryTool = React.lazy(() => import('./pages/admin/KycExpiryTool'));
const AdminAddProvider = React.lazy(() => import('./pages/admin/AddProvider'));
const AdminUsersManagement = React.lazy(() => import('./pages/admin/UsersManagement'));
const AdminVehicleApiSettings = React.lazy(() => import('./pages/admin/VehicleApiSettings'));
const AdminVINDecode = React.lazy(() => import('./pages/admin/AdminVINDecode'));
const AdminDocs = React.lazy(() => import('./pages/admin/AdminDocs'));
const AdminRequestDetail = React.lazy(() => import('./pages/admin/AdminRequestDetail'));
const AdminVehicleSystemStatus = React.lazy(() => import('./pages/admin/VehicleSystemStatus'));
const AdminContentPages = React.lazy(() => import('./pages/admin/ContentPages'));
const AdminMenuManager = React.lazy(() => import('./pages/admin/MenuManager'));
const AdminUITextManager = React.lazy(() => import('./pages/admin/UITextManager'));
const AdminSEOMetaManager = React.lazy(() => import('./pages/admin/SEOMetaManager'));
const AdminPaymentsDashboard = React.lazy(() => import('./pages/admin/PaymentsDashboard'));
const AdminEmailLogs = React.lazy(() => import('./pages/admin/EmailLogs'));
const AdminIntegrationSettings = React.lazy(() => import('./pages/admin/IntegrationSettings'));
const AdminLeadManagement = React.lazy(() => import('./pages/admin/LeadManagement'));
const AdminCampaignManager = React.lazy(() => import('./pages/admin/CampaignManager'));
const AdminLeadSourceDiscovery = React.lazy(() => import('./pages/admin/LeadSourceDiscovery'));
const AdminMediaUploads = React.lazy(() => import('./pages/admin/MediaUploads'));

// Monetization (Phase M1)
const MonetizationBillingOverview = React.lazy(() => import('./pages/admin/monetization/BillingOverview'));
const MonetizationPlansList = React.lazy(() => import('./pages/admin/monetization/PlansList'));
const MonetizationSubscriptionsList = React.lazy(() => import('./pages/admin/monetization/SubscriptionsList'));
const MonetizationSubscriptionDetail = React.lazy(() => import('./pages/admin/monetization/SubscriptionDetail'));
const MonetizationUsageLimits = React.lazy(() => import('./pages/admin/monetization/UsageLimits'));
const MonetizationBillingOrdersList = React.lazy(() => import('./pages/admin/monetization/BillingOrdersList'));
const MonetizationInvoicesList = React.lazy(() => import('./pages/admin/monetization/InvoicesList'));
const MonetizationCreditNotesList = React.lazy(() => import('./pages/admin/monetization/CreditNotesList'));
const MonetizationEmailLogsList = React.lazy(() => import('./pages/admin/monetization/EmailLogsList'));
const MonetizationCustomerProducts = React.lazy(() => import('./pages/admin/monetization/CustomerProductsList'));
const AdminCustomerBilling = React.lazy(() => import('./pages/admin/monetization/AdminCustomerBilling'));

// Public Pages
const PublicPage = React.lazy(() => import('./pages/PublicPage'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
// FC1 — Find & Claim Your Business
const ListYourBusiness = React.lazy(() => import('./pages/public/ListYourBusiness'));
const FindMyBusiness = React.lazy(() => import('./pages/public/FindMyBusiness'));
// UAT-4 — Public Provider Profiles
const GaragePublicProfile = React.lazy(() => import('./pages/public/GaragePublicProfile'));
const DealerPublicProfile = React.lazy(() => import('./pages/public/DealerPublicProfile'));
// UAT-6 — Support System
const TesterRecruitment = React.lazy(() => import('./pages/public/TesterRecruitment'));
const TesterOnboarding = React.lazy(() => import('./pages/public/TesterOnboarding'));
const TesterCampaigns = React.lazy(() => import('./pages/admin/TesterCampaigns'));
const ContactSupport = React.lazy(() => import('./pages/public/ContactSupport'));
const AccountDeletionRequest = React.lazy(() => import('./pages/public/AccountDeletionRequest'));
const PartsPurchaseHistory = React.lazy(() => import('./pages/customer/PartsPurchaseHistory'));
const SupportAssistant = React.lazy(() => import('./pages/customer/SupportAssistant'));
const MyTickets = React.lazy(() => import('./pages/customer/MyTickets'));
const SupportInbox = React.lazy(() => import('./pages/admin/SupportInbox'));
const SupportTicketDetail = React.lazy(() => import('./pages/admin/SupportTicketDetail'));

// Protected Route Component
function ProtectedRoute({ children, allowedRoles }) {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <FullPageLoading />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'moderator') {
      return <Navigate to="/admin/kyc" replace />;
    } else if (user?.role === 'garage_owner' || user?.role === 'garage_staff') {
      return <Navigate to="/garage/dashboard" replace />;
    } else if (user?.role === 'dealer_owner') {
      return <Navigate to="/dealer/dashboard" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
}

// Placeholder for routes not yet implemented
const ComingSoon = ({ titleKey, title }) => {
  const { t } = useTranslation('shared');
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
      <h2 className="font-heading text-2xl font-bold mb-2">{titleKey ? t(titleKey) : title}</h2>
      <p className="text-muted-foreground">{t('comingSoon.body')}</p>
    </div>
  );
};

// Root Route: show Landing for visitors; redirect authenticated users
// to their role-based dashboard.
function RootRoute() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <FullPageLoading />;
  }

  if (isAuthenticated) {
    const role = user?.role;
    if (role === 'admin' || role === 'super_admin') {
      return <Navigate to="/admin" replace />;
    }
    if (role === 'moderator') {
      return <Navigate to="/admin/kyc" replace />;
    }
    if (role === 'garage_owner' || role === 'garage_staff') {
      return <Navigate to="/garage/dashboard" replace />;
    }
    if (role === 'dealer_owner') {
      return <Navigate to="/dealer/dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <Landing />;
}

function AppRoutes() {
  const { t } = useTranslation('messaging');
  const { capabilities } = useSiteSettings();
  const featureRoute = (enabled, element, fallback) => (
    enabled ? element : <Navigate to={fallback} replace />
  );

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<RootRoute />} />
      <Route path="/login" element={<Login />} />
      <Route path="/search" element={<SearchGarages />} />
      <Route path="/specialists" element={<SearchGarages />} />

      {/* ============ FC1 — Find & Claim Your Business ============ */}
      <Route path="/list-your-business" element={<ListYourBusiness />} />
      <Route path="/find-my-business" element={<FindMyBusiness />} />
      {/* Claim wizard entry — preserve the selected lead for confirmation. */}
      <Route path="/claim/:leadId" element={
        <ProtectedRoute allowedRoles={['garage_owner', 'garage_staff', 'dealer_owner']}>
          <ProviderOnboarding />
        </ProtectedRoute>
      } />

      {/* ============ REPAIR FLOW ============ */}
      <Route path="/repair/*" element={<ProtectedRoute allowedRoles={['customer']}><RepairFlow /></ProtectedRoute>} />

      {/* ============ PARTS FLOW ============ */}
      <Route path="/parts/*" element={<ProtectedRoute allowedRoles={['customer']}><PartsFlow /></ProtectedRoute>} />

      {/* Request type selection (Repair vs Buy Parts) */}
      <Route path="/new-request" element={<ProtectedRoute allowedRoles={['customer']}><RequestTypeSelect /></ProtectedRoute>} />

      {/* Legacy redirects */}
      <Route path="/posts/new" element={<Navigate to="/new-request" replace />} />

      {/* Customer Dashboard */}
      <Route
        path="/dashboard"
        element={<ProtectedRoute allowedRoles={['customer']}><DashboardLayout /></ProtectedRoute>}
      >
        <Route index element={<CustomerDashboard />} />
      </Route>

      <Route
        element={<ProtectedRoute allowedRoles={['customer']}><DashboardLayout /></ProtectedRoute>}
      >
        {/* Customer request management — wrapped in DashboardLayout for consistent Header + Sidebar */}
        <Route path="/my-requests" element={<MyRequests />} />
        <Route path="/my-requests/:id" element={<RequestDetail />} />
        <Route path="/parts-purchase-history" element={featureRoute(capabilities.transactional_parts_enabled, <PartsPurchaseHistory />, '/my-requests')} />
        <Route path="/vehicles" element={<VehiclesList />} />
        <Route path="/vehicles/new" element={<VehicleForm />} />
        <Route path="/vehicles/:id/edit" element={<VehicleForm />} />
        <Route path="/vehicles/:id" element={<VehicleProfile />} />
        <Route path="/posts" element={featureRoute(capabilities.bookings_enabled, <MyPosts />, '/my-requests')} />
        <Route path="/posts/:id" element={featureRoute(capabilities.bookings_enabled, <PostDetails />, '/my-requests')} />
        <Route path="/bookings" element={featureRoute(capabilities.bookings_enabled, <BookingsList />, '/my-requests')} />
        <Route path="/bookings/:id" element={featureRoute(capabilities.bookings_enabled, <BookingDetails />, '/my-requests')} />
        <Route path="/chats" element={<RouteErrorBoundary label={t('routeLabel')} homePath="/dashboard"><MessagesPage /></RouteErrorBoundary>} />
        <Route path="/messages" element={<RouteErrorBoundary label={t('routeLabel')} homePath="/dashboard"><MessagesPage /></RouteErrorBoundary>} />
        <Route path="/saved-searches" element={<SavedSearches />} />
        <Route path="/vin-decode" element={<VINDecodePage />} />
        <Route path="/vehicle-care" element={<EnrollVehicleCarePage />} />
        <Route path="/care/success" element={<CareSuccess />} />
        <Route path="/care/cancel" element={<CareCancel />} />
        <Route path="/billing" element={<InvoicesPage />} />
        <Route path="/reviews" element={<ComingSoon title="Reviews" />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/support/tickets" element={<MyTickets />} />
        <Route path="/support/tickets/:id" element={<MyTickets />} />
        <Route path="/settings" element={<Navigate to="/profile" replace />} />
      </Route>


      <Route path="/subscription/success" element={<ProtectedRoute allowedRoles={['garage_owner', 'dealer_owner']}><SubscriptionSuccess /></ProtectedRoute>} />

      {/* ============ GARAGE ROUTES ============ */}
      <Route
        path="/garage"
        element={<ProtectedRoute allowedRoles={['garage_owner', 'garage_staff']}><DashboardLayout /></ProtectedRoute>}
      >
        <Route path="dashboard" element={<GarageDashboard />} />
        <Route path="profile" element={<ProviderProfile />} />
        <Route path="account" element={<ProviderAccountSettings />} />
        <Route path="support" element={<SupportAssistant />} />
        <Route path="support/tickets" element={<MyTickets />} />
        <Route path="support/tickets/:id" element={<MyTickets />} />
        <Route path="kyc" element={<Navigate to="/garage/profile?tab=documents" replace />} />
        {/* UAT-1: garage `/leads` retired. Customer repair requests now
            appear under `/garage/incoming` (relabelled "Leads" in the sidebar).
            The acquisition leads system stays admin-only at /admin/leads. */}
        <Route path="leads" element={<Navigate to="/garage/incoming" replace />} />
        <Route path="leads/:id" element={<Navigate to="/garage/incoming" replace />} />
        {/* Garage Repair Incoming (Phase 7) — wrapped in DashboardLayout for consistent Header + Sidebar */}
        <Route path="incoming" element={<IncomingRepairRequests />} />
        <Route path="incoming/:id" element={<GarageRepairRequestDetail />} />
        {/* Standalone VIN Lookup removed for providers — redirect any old bookmarks back to the dashboard. */}
        <Route path="vin-lookup" element={<Navigate to="/garage/dashboard" replace />} />
        <Route path="subscription" element={<ProviderSubscription />} />
        <Route path="my-plan" element={<ProviderMyPlan />} />
        <Route path="my-usage" element={<ProviderMyUsage />} />
        <Route path="billing" element={<ProviderMyBilling />} />
        <Route path="subscription/success" element={<SubscriptionSuccess />} />
        <Route path="bookings" element={featureRoute(capabilities.bookings_enabled, <GarageBookings />, '/garage/incoming')} />
        <Route path="bookings/:id" element={<ComingSoon titleKey="comingSoon.bookingDetails" />} />
        <Route path="jobs" element={featureRoute(capabilities.job_management_enabled, <JobCardsList />, '/garage/incoming')} />
        <Route path="jobs/:jobId" element={featureRoute(capabilities.job_management_enabled, <JobCardManagement />, '/garage/incoming')} />
        {/* Garage → Dealer RFQ inbox (B2B parts sourcing) */}
        <Route path="rfqs" element={<MyRfqs />} />
        <Route path="rfqs/:id" element={<RfqDetail />} />
        <Route path="chats" element={<RouteErrorBoundary label={t('routeLabel')} homePath="/garage/dashboard"><MessagesPage /></RouteErrorBoundary>} />
        <Route path="messages" element={<RouteErrorBoundary label={t('routeLabel')} homePath="/garage/dashboard"><MessagesPage /></RouteErrorBoundary>} />
        <Route path="saved-searches" element={<SavedSearches />} />
        <Route path="analytics" element={<ComingSoon titleKey="comingSoon.analytics" />} />
      </Route>

      {/* Garage Source-Parts Flow (standalone, wrapped with FlowProvider) */}
      <Route path="/garage/source-parts" element={<ProtectedRoute allowedRoles={['garage_owner', 'garage_staff']}><GaragePartsFlow basePath="/garage" /></ProtectedRoute>} />
      <Route path="/garage/vehicle" element={<ProtectedRoute allowedRoles={['garage_owner', 'garage_staff']}><GaragePartsFlow basePath="/garage" /></ProtectedRoute>} />
      <Route path="/garage/parts" element={<ProtectedRoute allowedRoles={['garage_owner', 'garage_staff']}><GaragePartsFlow basePath="/garage" /></ProtectedRoute>} />
      <Route path="/garage/review" element={<ProtectedRoute allowedRoles={['garage_owner', 'garage_staff']}><GaragePartsFlow basePath="/garage" /></ProtectedRoute>} />
      <Route path="/garage/result" element={<ProtectedRoute allowedRoles={['garage_owner', 'garage_staff']}><GaragePartsFlow basePath="/garage" /></ProtectedRoute>} />

      {/* Garage Onboarding */}
      <Route path="/garage/onboarding" element={<ProtectedRoute allowedRoles={['customer', 'garage_owner', 'garage_staff']}><GarageOnboarding /></ProtectedRoute>} />
      <Route path="/garage/register" element={<Login />} />

      {/* Unified Provider Onboarding */}
      <Route path="/provider/onboarding" element={<ProtectedRoute allowedRoles={['customer', 'garage_owner', 'garage_staff', 'dealer_owner']}><ProviderOnboarding /></ProtectedRoute>} />
      <Route path="/list-business" element={<Login />} />

      {/* ============ DEALER ROUTES ============ */}
      <Route
        path="/dealer"
        element={<ProtectedRoute allowedRoles={['dealer_owner']}><DashboardLayout /></ProtectedRoute>}
      >
        <Route path="dashboard" element={<DealerDashboard />} />
        {/* UAT-1: dealer `/leads` retired. Customer parts requests now appear
            under `/dealer/requests` (relabelled "Leads" in the sidebar). The
            acquisition leads system stays admin-only at /admin/leads. */}
        <Route path="leads" element={<Navigate to="/dealer/requests" replace />} />
        {/* Dealer parts requests — wrapped in DashboardLayout for consistent Header + Sidebar */}
        <Route path="requests" element={<IncomingRequests />} />
        <Route path="requests/:id" element={<DealerRequestDetail />} />
        {/* Dealer → Dealer RFQ (D2D parts sourcing) — requesting-dealer surface */}
        <Route path="source-parts" element={<DealerSourceParts />} />
        <Route path="rfqs" element={<DealerMyRfqs />} />
        <Route path="rfqs/:id" element={<DealerRfqDetail />} />
        <Route path="kyc" element={<Navigate to="/dealer/profile?tab=documents" replace />} />
        {/* Standalone VIN Lookup removed for providers — redirect any old bookmarks back to the dashboard. */}
        <Route path="vin-lookup" element={<Navigate to="/dealer/dashboard" replace />} />
        <Route path="subscription" element={<ProviderSubscription />} />
        <Route path="my-plan" element={<ProviderMyPlan />} />
        <Route path="my-usage" element={<ProviderMyUsage />} />
        <Route path="billing" element={<ProviderMyBilling />} />
        <Route path="subscription/success" element={<SubscriptionSuccess />} />
        <Route path="profile" element={<ProviderProfile />} />
        <Route path="account" element={<ProviderAccountSettings />} />
        <Route path="support" element={<SupportAssistant />} />
        <Route path="support/tickets" element={<MyTickets />} />
        <Route path="support/tickets/:id" element={<MyTickets />} />
        <Route path="chats" element={<RouteErrorBoundary label={t('routeLabel')} homePath="/dealer/dashboard"><MessagesPage /></RouteErrorBoundary>} />
        <Route path="messages" element={<RouteErrorBoundary label={t('routeLabel')} homePath="/dealer/dashboard"><MessagesPage /></RouteErrorBoundary>} />
        <Route path="saved-searches" element={<SavedSearches />} />
      </Route>

      {/* ============ ADMIN ROUTES ============ */}
      <Route
        path="/admin"
        element={<ProtectedRoute allowedRoles={['admin', 'super_admin', 'moderator']}><AdminLayout /></ProtectedRoute>}
      >
        <Route index element={<AdminDashboardEnhanced />} />
        <Route path="providers" element={<AdminProviders />} />
        <Route path="providers/add" element={<AdminAddProvider />} />
        <Route path="providers/:id" element={<AdminProviderDetail />} />
        <Route path="kyc" element={<AdminKYCVerification />} />
        <Route path="kyc-expiry" element={<AdminKycExpiryTool />} />
        <Route path="verification" element={<AdminKYCVerification />} />
        <Route path="subscriptions" element={<Navigate to="/admin/monetization/subscriptions" replace />} />
        <Route path="pricing-plans" element={<Navigate to="/admin/monetization/plans" replace />} />
        <Route path="currencies" element={<AdminCurrencies />} />
        <Route path="requests" element={<AdminRequests />} />
        <Route path="requests/:id" element={<AdminRequestDetail />} />
        <Route path="users" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminUsersManagement /></ProtectedRoute>} />
        <Route path="master-data" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminMasterData /></ProtectedRoute>} />
        <Route path="audit-logs" element={<AdminAuditLogs />} />
        <Route path="vehicle-ownership" element={<AdminVehicleOwnership />} />
        <Route path="provider-claims" element={<AdminProviderClaims />} />
        {/* Legacy /admin/settings retired — redirect to authoritative /admin/system-settings */}
        <Route path="settings" element={<Navigate to="/admin/system-settings" replace />} />
        <Route path="system-settings" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminSystemSettings /></ProtectedRoute>} />
        <Route path="settings/vehicle-apis" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminVehicleApiSettings /></ProtectedRoute>} />
        <Route path="vin-decode" element={<AdminVINDecode />} />
        <Route path="docs" element={<AdminDocs />} />
        <Route path="vehicle-system-status" element={<AdminVehicleSystemStatus />} />
        <Route path="content-pages" element={<AdminContentPages />} />
        <Route path="menu-manager" element={<AdminMenuManager />} />
        <Route path="menu" element={<Navigate to="/admin/menu-manager" replace />} />
        <Route path="ui-text" element={<AdminUITextManager />} />
        <Route path="seo-meta" element={<AdminSEOMetaManager />} />
        <Route path="payments" element={<AdminPaymentsDashboard />} />
        <Route path="email-logs" element={<AdminEmailLogs />} />
        <Route path="integrations" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminIntegrationSettings /></ProtectedRoute>} />
        <Route path="integrations/firebase" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><AdminFirebase /></ProtectedRoute>} />
        <Route path="leads" element={<AdminLeadManagement />} />
        <Route path="leads/callers" element={<ProtectedRoute allowedRoles={['admin', 'super_admin']}><CallerManagement /></ProtectedRoute>} />
        <Route path="campaigns" element={<AdminCampaignManager />} />
        <Route path="lead-sources" element={<AdminLeadSourceDiscovery />} />
        <Route path="media-uploads" element={<AdminMediaUploads />} />

        {/* UAT-6 — Support System */}
        <Route path="support" element={<SupportInbox />} />
        <Route path="support/:id" element={<SupportTicketDetail />} />

        {/* Monetization (Phase M1) */}
        <Route path="monetization/overview" element={<MonetizationBillingOverview />} />
        <Route path="monetization/plans" element={<MonetizationPlansList />} />
        <Route path="testers" element={<TesterCampaigns />} />
        <Route path="monetization/customer-products" element={<MonetizationCustomerProducts />} />
        <Route path="monetization/customer-billing" element={<AdminCustomerBilling />} />
        <Route path="monetization/subscriptions" element={<MonetizationSubscriptionsList />} />
        <Route path="monetization/subscriptions/:id" element={<MonetizationSubscriptionDetail />} />
        <Route path="monetization/usage" element={<MonetizationUsageLimits />} />
        {/* Billing (Phase M2-B) */}
        <Route path="monetization/orders" element={<MonetizationBillingOrdersList />} />
        <Route path="monetization/invoices" element={<MonetizationInvoicesList />} />
        <Route path="monetization/credit-notes" element={<MonetizationCreditNotesList />} />
        <Route path="monetization/email-logs" element={<MonetizationEmailLogsList />} />
      </Route>

      {/* Public Garage Profile */}
      <Route path="/garages/:id" element={<GarageProfile />} />

      {/* UAT-4 — Public Provider Profiles (/providers/{kind}/:id) */}
      <Route path="/providers/garage/:id" element={<GaragePublicProfile />} />
      <Route path="/providers/dealer/:id" element={<DealerPublicProfile />} />

      {/* UAT-6 — Public Contact / Support form */}
      <Route path="/caller" element={<CallerWorkspace />} />
      <Route path="/testing" element={<TesterRecruitment />} />
      <Route path="/testing/onboarding" element={<ProtectedRoute allowedRoles={['customer', 'garage_owner', 'dealer_owner']}><TesterOnboarding /></ProtectedRoute>} />
      <Route path="/contact" element={<ContactSupport />} />
      {/* UAT-6 — AI Support Assistant (chat-first, escalates to tickets) */}
      <Route path="/support" element={<SupportAssistant />} />
      <Route path="/account-deletion" element={<AccountDeletionRequest />} />

      {/* Public Static Pages (Phase 10) */}
      <Route path="/page/:slug" element={<PublicPage />} />
      <Route path="/terms" element={<Navigate to="/page/terms" replace />} />
      <Route path="/privacy" element={<Navigate to="/page/privacy" replace />} />
      <Route path="/partner-agreement" element={<Navigate to="/page/partner-agreement" replace />} />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Canonical provider billing route (accessible by garage + dealer roles) */}
      <Route
        path="/provider/billing"
        element={
          <ProtectedRoute allowedRoles={['garage_owner', 'garage_staff', 'dealer_owner']}>
            <ProviderMyBilling />
          </ProtectedRoute>
        }
      />

      {/* 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <SiteSettingsProvider>
    <AuthProvider>
    <UITextProvider>
      <BrowserRouter>
        <DirectionProvider>
          <NotificationRealtimeBridge />
          <React.Suspense fallback={<FullPageLoading />}><AppRoutes /></React.Suspense>
          <DirectionalToaster
            richColors
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Manrope, sans-serif',
              },
            }}
          />
        </DirectionProvider>
      </BrowserRouter>
    </UITextProvider>
    </AuthProvider>
    </SiteSettingsProvider>
    </HelmetProvider>
  );
}

export default App;
